import type { EloRating } from "../types.js";
import type { GraphScores } from "../graph/scores.js";
import type { MasteryState } from "./masteryState.js";
import { topologicalOrder } from "../graph/topological.js";
import type { DiGraph } from "../graph/digraph.js";

const DEFAULT_TARGET: EloRating = 1400;

/**
 * Single-concept “practice need” score (same weighting as `recommendNextConcept`):
 * weak mastery (gap to target) × central PageRank × bias toward earlier topo order.
 */
export function conceptUrgency(
  scores: GraphScores,
  state: MasteryState,
  conceptId: string,
  targetElo: EloRating = DEFAULT_TARGET
): number {
  const m = state.byConcept[conceptId] ?? state.defaultRating;
  const gap = Math.max(0, targetElo - m);
  const pr = scores.pageRank.get(conceptId) ?? 0;
  const topo = scores.topoRank.get(conceptId) ?? 0.5;
  return pr * (0.5 + gap / 200) * (0.4 + 0.6 * (1 - topo));
}

/**
 * Suggests a concept the learner is **weak in** (below `targetElo` relative to the gap) weighted
 * by **PageRank** and biased toward an **earlier** spot in a teaching `topo` order in ties.
 */
export function recommendNextConcept(
  g: DiGraph,
  scores: GraphScores,
  state: MasteryState,
  options: { targetElo?: EloRating; conceptIds?: string[] } = {}
): string | null {
  const order = topologicalOrder(g);
  if (!order.ok) return null;
  const target = options.targetElo ?? DEFAULT_TARGET;
  const ids = options.conceptIds ?? order.order;
  if (ids.length === 0) return null;
  const scored = ids.map((id) => {
    const s = conceptUrgency(scores, state, id, target);
    const m = state.byConcept[id] ?? state.defaultRating;
    const gap = Math.max(0, target - m);
    const topo = scores.topoRank.get(id) ?? 0.5;
    return { id, s, t: 1 - topo, gap };
  });
  scored.sort((a, b) => b.s - a.s || a.t - b.t || a.id.localeCompare(b.id));
  return scored[0]!.id;
}
